/**
 * W-8IMY Models
 * Clean separation between API response model and form model with mapping logic
 */

// W8IMY Response Model - Fields that come from API endpoint
import { determineStateFieldType, parseAddressComponents } from "../utils/formUtils";
export class W8IMYResponseModel {
  // Basic identification fields
  "UniqueIdentifier": string = "";
  "Name of organization": string = "";
  "Country_of_incorporation_organization": string = "";
  "Form_name": string = "W-8IMY";
  "Form_revision": string = "";

  // Address fields
  "Permanent residence address": string = "";
  "Permanent city address": string = "";
  "Permanent address country": string = "";
  "Mailing address": string = "";
  "Mailing address city": string = "";
  "Mailing address country": string = "";

  // // Tax identification fields
  // // 'U.S. taxpayer identification number': string = '';
  // // 'GIIN': string = '';
  "IMY-9B_foreign_tax_identifying_number": string = "";
  "IMY-10_reference_number": string = "";
  "Print_name": string = "";
  "Signature_date": string = "";
  "IMY-8_us_tin_qi_ein": string = "";
  "IMY-8_us_tin_wp_ein": string = "";
  "IMY-8_us_tin_wt_ein": string = "";
  "IMY-8_us_tin_ein": string = "";

  // Dynamic fields support
  [key: string]: any;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

// W8IMY Form Model - Complete form fields (more comprehensive)
export class W8IMYFormModel {
  // Dynamic fields support
  [key: string]: any;

  // Basic identification fields (4 fields)
  "UniqueIdentifier": string = "";
  "Name of organization": string = "";
  "Country_of_incorporation_organization": string = "";
  "Other": string = "";

  // Address fields - only used ones
  "Permanent residence address": string = "";
  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address country": string = "";
  "Permanent address other": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";

  "Mailing address": string = "";
  "Mailing address line 2": string = "";
  "Mailing address line 3": string = "";
  "Mailing city address": string = "";
  "Mailing address state USCA": string = "";
  "Mailing address state NonUS": string = "";
  "Mailing address zip": string = "";
  "Mailing address country": string = "";
  "Mailing address other": string = "";
  "Mailing address is care of": string = "no";
  "Mailing address is po box": string = "no";
  "Mailing address is rural route": string = "no";

  // Taxpayer identification fields (8 fields)
  "Taxpayer type": string = "";
  "U.S. taxpayer identification number": string = "";
  "GIIN": string = "";
  "GIINCheckBox": boolean = false;
  "GIIN Applied For": boolean = false;
  "Foreign TIN": boolean = false;
  "IMY-9B_foreign_tax_identifying_number": string = "";
  "IMY-10_reference_number": string = "";
  "Print_name": string = "";
  "Form_name": string = "W-8IMY";
  "Form_revision": string = "";

  // TIN Type fields - removed duplicates, using response data field names instead

  // Part II Chapter 3 Status (9 fields)
  "IMY-4_QI": boolean = false;
  "IMY-4_Non_qualified_intermediary": boolean = false;
  "IMY-4_Withholding_foreign_partnership": boolean = false;
  "IMY-4_Withholding_foreign_trust": boolean = false;
  "IMY-4_Non_withholding_foreign_partnership": boolean = false;
  "IMY-4_Non_withholding_foreign_simple_trust": boolean = false;
  "IMY-4_Non_withholding_foreign_grantor_trust": boolean = false;
  "IMY-4_Territory_financial_institution": boolean = false;
  "IMY-4_US_branch": boolean = false;
  "IMY-branchCity_State_postal": string = "";
  "IMY-branchCity_State_state": string = "";

  // Part III Chapter 4 Status (ALL fields from API response)
  "IMY-11_Branch_non_participating_FFI": boolean = false;
  "IMY-11_Participating_FFI": boolean = false;
  "IMY-11_Reporting_model1_FFI": boolean = false;
  "IMY-11_Reporting_model2_FFI": boolean = false;
  "IMY-11_US_Branch": boolean = false;

  // Only fields actually used in FORM or XML - exact names from response data
  "IMY-8_us_tin": string = "";
  "IMY-8_us_tin_qi_ein": string = "";
  "IMY-8_us_tin_wp_ein": string = "";
  "IMY-8_us_tin_wt_ein": string = "";
  "IMY-8_us_tin_ein": string = "";
  "IMY-9A_giin": string = "";
  "IMY-13_disregarded_entity_giin": string = "";
  "IMY-32_model1_iga": string = "";
  "IMY-32_model2_iga": string = "";
  "IMY-32_treated_as": string = "";
  "IMY-32_trustee_us": string = "";
  "IMY-32_trustee_foreign": string = "";
  "IMY-23A_name_of_sponsoring_entity": string = "";
  "IMY-27A_sponsored_investment_vehicle_name": string = "";
  "IMY-37B_affiliate_securities_market": string = "";
  "IMY-41_sponsor_name": string = "";

  // Legacy fields - remove unused ones

  // Part IV QI Certification (10+ fields)
  "IMY-14_Certification_Qualified_Intermediary": boolean = false;

  "IMY-19D_US_person_for_sales_of_interests": boolean = false;

  // Owner-Documented FFI fields
  // 'IMY-24A_Owner-documented_FFI_requirements': boolean = false;
  "IMY-24B_FFI_owner_reporting_statement": boolean = false;

  "IMY-36_Liquidation_or_bankruptcy": boolean = false;

  "IMY-37A_securities_market": string = "";

  "IMY-37B_affiliate_entity_name": string = "";

  // Certification fields
  "Signature": string = "";
  "Signature_date": string = "";
  "Signature_unreadable": boolean = false;
  "Signing_capacity": string = "";
  "isSigned": string = "no";
  "isModified": string = "no";

  // Mapping control field - prevents re-mapping when already done
  isMapDone: boolean = false;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: Record<string, string> = {};

    // Updated required field validations per user request:
    // Only UniqueIdentifier, Name of organization, Permanent residence address, and 5 radio buttons
    if (!this["UniqueIdentifier"]) {
      errors["UniqueIdentifier"] = "Unique Identifier is required";
    }

    if (!this["Name of organization"]) {
      errors["Name of organization"] = "Organization name is required";
    }

    // Required certification fields
    if (!this["isSigned"]) {
      errors["isSigned"] = "Please provide a value for Is signed";
    }

    if (!this["isModified"]) {
      errors["isModified"] = "Please provide a value for Has the form Certification been modified";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      fieldErrors: errors,
    };
  }

  // Generate XML representation with proper field mapping
  generateXML(): string {
    const xmlData = {};

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += "<W8IMYForm>\n";

    Object.entries(xmlData).forEach(([key, value]) => {
      xml += `  <${key}>${this.escapeXml(String(value))}</${key}>\n`;
    });

    xml += "</W8IMYForm>";

    return xml;
  }

  /**
   * Escape XML special characters
   */
  private escapeXml(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  fieldErrors: Record<string, string>;
}

// Field Mapper - Converts between API response and form model
export class W8IMYFieldMapper {
  /**
   * Map API response to form model
   * TASK 2: Handle response data field mapping properly
   */
  static mapResponseToForm(responseModel: W8IMYResponseModel): W8IMYFormModel {
    const formModel = new W8IMYFormModel();

    // 🛡️ CRITICAL PROTECTION: Check if mapping is already done to prevent re-mapping
    // This prevents overwriting user changes and maintains data integrity
    if (responseModel.isMapDone || formModel.isMapDone) {
      console.log(
        "🛡️ W8IMY mapping already done (isMapDone=true), skipping field mapping to preserve user data"
      );

      // If mapping is done, copy the response data directly without field transformations
      Object.keys(responseModel).forEach((key) => {
        if (responseModel[key] !== undefined && typeof responseModel[key] !== "function") {
          (formModel as any)[key] = responseModel[key];
        }
      });

      // Ensure isMapDone flag is preserved
      formModel.isMapDone = true;
      return formModel;
    }

    // Direct field mappings (fields that exist in BOTH API response and form models)
    const directMappings = [
      // Basic fields
      "UniqueIdentifier",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Apply special field mappings (including address mappings)
    this.mapSpecialFields(responseModel, formModel);

    // Apply boolean conversion AFTER all mappings
    this.convertBooleanFields(formModel);

    // Set mapping done flag to prevent re-mapping
    formModel.isMapDone = true;
    console.log("W8IMY field mapping completed");

    return formModel;
  }

  /**
   * Convert boolean fields that may come as strings from API to actual booleans
   */
  private static convertBooleanFields(formModel: W8IMYFormModel): void {
    const booleanFields = [
      // 'GIINCheckBox',
      "GIIN Applied For",
    ];

    booleanFields.forEach((field) => {
      if (formModel[field] !== undefined) {
        const value = formModel[field];
        if (typeof value === "string") {
          formModel[field] =
            value.toLowerCase() === "true" ||
            value === "1" ||
            value.toLowerCase() === "yes" ||
            value.toLowerCase() === "selected";
        } else if (typeof value === "number") {
          formModel[field] = value === 1;
        } else {
          formModel[field] = !!value;
        }
      }
    });
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: W8IMYResponseModel,
    formModel: W8IMYFormModel
  ): void {
    // 🛡️ CRITICAL PROTECTION: Don't apply special field mappings if already mapped
    if (responseModel.isMapDone || formModel.isMapDone) {
      return;
    }

    // TASK 2: Map response data field names to form model field names
    this.mapResponseDataFields(responseModel, formModel);

    // Map address fields from API to form
    if (responseModel["Permanent residence address"]) {
      formModel["Permanent residence address"] = responseModel["Permanent residence address"];
    }

    if (responseModel["Permanent city address"]) {
      const addressComponents = parseAddressComponents(responseModel["Permanent city address"]);
      formModel["Permanent address city"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        responseModel["Permanent address country"],
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Permanent address state USCA"] = addressComponents.state;
        formModel["Permanent address state NonUS"] = "";
      } else {
        formModel["Permanent address state NonUS"] = addressComponents.state;
        formModel["Permanent address state USCA"] = "";
      }

      formModel["Permanent address zip"] = addressComponents.postalCode;
    }
    if (
      responseModel["IMY-12_disregarded_entity_city_state_of_branch"] &&
      formModel["isMapDone"] === false
    ) {
      const addressComponents = parseAddressComponents(
        responseModel["IMY-12_disregarded_entity_city_state_of_branch"]
      );
      formModel["IMY-12_disregarded_entity_city_state_of_branch"] = addressComponents.city;

      // Only set state and postal from parsing if they weren't already mapped directly from API
      if (!formModel["IMY-branchCity_State_state"]) {
        formModel["IMY-branchCity_State_state"] = addressComponents.state;
      }
      if (!formModel["IMY-branchCity_State_postal"]) {
        formModel["IMY-branchCity_State_postal"] = addressComponents.postalCode;
      }
    }

    if (responseModel["Permanent address country"]) {
      formModel["Permanent address country"] = responseModel["Permanent address country"];
    }

    if (responseModel["Mailing address"]) {
      formModel["Mailing address"] = responseModel["Mailing address"];
    }

    if (responseModel["Mailing address city"]) {
      const addressComponents = parseAddressComponents(responseModel["Mailing address city"]);
      formModel["Mailing city address"] = addressComponents.city;
      const stateFieldType = determineStateFieldType(
        responseModel["Mailing address country"],
        addressComponents.state
      );

      if (stateFieldType === "USCA") {
        formModel["Mailing address state USCA"] = addressComponents.state;
        formModel["Mailing address state NonUS"] = "";
      } else {
        formModel["Mailing address state NonUS"] = addressComponents.state;
        formModel["Mailing address state USCA"] = "";
      }

      formModel["Mailing address zip"] = addressComponents.postalCode;
    }

    if (responseModel["Mailing address country"]) {
      formModel["Mailing address country"] = responseModel["Mailing address country"];
    }

    // Map signature fields
    if (responseModel["Signature"]) {
      formModel["Signature"] = responseModel["Signature"];
    }

    if (responseModel["Signature_date"]) {
      formModel["Signature_date"] = responseModel["Signature_date"];
    }
  }

  /**
   * TASK 2: Map response data field names to form model field names
   * Handle the specific response format from database
   */
  private static mapResponseDataFields(responseModel: any, formModel: W8IMYFormModel): void {
    // If form is already mapped, don't apply field mappings to preserve data integrity
    if (responseModel.isMapDone || formModel.isMapDone) {
      return;
    }

    // Special field mappings from response to form model
    const specialMappings = {
      "IMY-8_us_tin": "U.S. taxpayer identification number",
      "IMY-9A_giin": "GIIN",
      "IMY-9B_foreign_tax_identifying_number": "IMY-9B_foreign_tax_identifying_number",
      "IMY-10_reference_number": "IMY-10_reference_number",
      "Entity Type": "EntityType",
    };

    // Apply special mappings
    Object.entries(specialMappings).forEach(([responseField, formField]) => {
      if (responseModel[responseField] !== undefined) {
        if (
          typeof responseModel[responseField] === "string" &&
          responseModel[responseField] === "selected"
        ) {
          formModel[formField] = true;
        } else {
          formModel[formField] = responseModel[responseField];
        }
      }
    });

    // Handle all other response fields that might be using different naming conventions
    Object.keys(responseModel).forEach((responseField) => {
      // Convert "selected" strings to boolean true
      if (responseModel[responseField] === "selected") {
        // Try to find matching form field
        const formField = this.findMatchingFormField(responseField, formModel);
        if (formField) {
          formModel[formField] = true;
        }
      }

      // Handle API fields with underscores/dashes - map to form fields with spaces
      const formFieldName = responseField.replace(/_/g, " ").replace(/-/g, " ");
      if (formFieldName !== responseField && (formModel as any)[formFieldName] !== undefined) {
        (formModel as any)[formFieldName] = responseModel[responseField];
      }
    });
  }

  /**
   * Find matching form field for response field
   */
  private static findMatchingFormField(
    responseField: string,
    formModel: W8IMYFormModel
  ): string | null {
    // Direct match
    if (formModel.hasOwnProperty(responseField)) {
      return responseField;
    }

    // Try common naming pattern conversions
    const patterns = [
      // Convert lowercase to mixed case
      responseField.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase()),
      // Convert snake_case to camelCase
      responseField.replace(/_(.)/g, (match, letter) => letter.toUpperCase()),
      // Try with first letter capitalized
      responseField.charAt(0).toUpperCase() + responseField.slice(1),
    ];

    for (const pattern of patterns) {
      if (formModel.hasOwnProperty(pattern)) {
        return pattern;
      }
    }

    return null;
  }
}
